import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React from 'react';

import { DISPLAY_DATE_FORMAT_LONG_WITH_DAY } from '../../../config/helpers';
import formatPrice from '../../../util/formatPrice';

import Icon from '../Atoms/Icon';
import Picture from '../Picture';
import {
  currencyCodeAndSymbolPropTypes,
  currencyDepositAndTotalPricePropTypesArrayPropTypes,
  findCurrencyItemFromListWithFallback,
} from '../../../util/currency';
import CompareItemButton from '../CompareItemButton';

const propTypes = exact({
  currentCurrency: currencyCodeAndSymbolPropTypes.isRequired,
  date: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  fromPriceText: PropTypes.string,
  image: PropTypes.object.isRequired,
  inclusions: PropTypes.array.isRequired,
  isSoldOut: PropTypes.bool.isRequired,
  tourKey: PropTypes.string.isRequired,
  limitedAvailability: PropTypes.bool.isRequired,
  limitedAvailabilityRemainingSpaces: PropTypes.number,
  limitedAvailabilityText: PropTypes.string,
  limitedAvailabilityTextOverride: PropTypes.string,
  pricePerCurrency: currencyDepositAndTotalPricePropTypesArrayPropTypes.isRequired,
  perPersonText: PropTypes.string,
  soldOutText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  tournamentName: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  useGridLayout: PropTypes.bool,
  viewTourText: PropTypes.string,
  waitingListLink: PropTypes.object,
  waitingListText: PropTypes.string,
});

const defaultProps = {
  fromPriceText: 'from',
  perPersonText: 'pp',
  limitedAvailabilityRemainingSpaces: null,
  limitedAvailabilityText: 'Limited Availability',
  limitedAvailabilityTextOverride: null,
  useGridLayout: false,
  viewTourText: 'view package',
  waitingListLink: null,
  waitingListText: null,
};

function Listing(props) {
  const totalPriceInSelectedCurrency = findCurrencyItemFromListWithFallback(
    props.pricePerCurrency,
    props.currentCurrency.code
  )?.totalPrice;
  return (
    <article className={`PackageListing ${props.useGridLayout ? 'PackageListing-grid' : ''}`}>
      <div className="PackageListing_content">
        <div className="PackageListing_imageContainer">
          {props.limitedAvailability && !props.limitedAvailabilityTextOverride && (
            <div className="PackageListing_limitedAvailability">
              {props.limitedAvailabilityText.replace(
                '{LimitedAvailabilityRemainingSpaces}',
                props.limitedAvailabilityRemainingSpaces
              )}
            </div>
          )}
          {props.limitedAvailability && props.limitedAvailabilityTextOverride && (
            <div className="PackageListing_limitedAvailability">
              {props.limitedAvailabilityTextOverride.replace(
                '{LimitedAvailabilityRemainingSpaces}',
                props.limitedAvailabilityRemainingSpaces
              )}
            </div>
          )}
          <CompareItemButton tourKey={props.tourKey} compareTextLabel="Compare" />
          {props.image && (
            <Picture
              {...props.image}
              className="PackageListing_image"
              sizes={[
                [100, 1.77], // from 0
                [100, 1.77], // from 480
                [50, 1.77], // from 768
                ['500', 1.77], // from 1024
                ['500', 1.77], // from 1440
              ]}
            />
          )}
        </div>
        <div className="PackageListing_body">
          <div className="PackageListing_copy">
            <div className="PackageListing_tournament">{props.tournamentName}</div>
            <h2 className="PackageListing_title">{props.title}</h2>
            <div className="PackageListing_description">{props.description}</div>
            <time className="PackageListing_date" dateTime={props.date}>
              {moment(props.date).format(DISPLAY_DATE_FORMAT_LONG_WITH_DAY)}
            </time>
          </div>
          <aside className="PackageListing_inclusions">
            <ul className="PackageListing_inclusionsList">
              {props.inclusions.map((inclusion) => (
                <li key={inclusion} className="PackageListing_inclusionsItem">
                  <Icon id={inclusion} blockName="PackageListing_inclusionsIcon" />
                </li>
              ))}
            </ul>
          </aside>
          {props.isSoldOut && props.waitingListLink === null && (
            <div className="PackageListing_soldOut">{props.soldOutText}</div>
          )}
          {props.waitingListLink != null && (
            <a href={props.url} className="PackageListing_link PackageListing_link-waitingList">
              <div className="PackageListing_soldOutText">{props.soldOutText}</div>
              <span className="PackageListing_cta">
                <span className="PackageListing_waitingListText">{props.waitingListText}</span>
                <Icon id="arrow-right" blockName="PackageListing_ctaIcon" />
              </span>
            </a>
          )}
          {!props.isSoldOut && props.waitingListLink === null && (
            <a href={props.url} className="PackageListing_link">
              <span className="PackageListing_price">
                {props.fromPriceText} {props.currentCurrency.symbol}
                {formatPrice(totalPriceInSelectedCurrency)}
                {props.perPersonText}
              </span>
              <span className="PackageListing_cta">
                <span className="PackageListing_ctaText">{props.viewTourText}</span>
                <Icon id="arrow-right" blockName="PackageListing_ctaIcon" />
              </span>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

Listing.propTypes = propTypes;
Listing.defaultProps = defaultProps;

export default Listing;
